import React, { useEffect, useMemo } from 'react'
import styles from './styles/h2h.module.scss'
import { Flag } from 'components/generic/Country'
import LoadMore from 'components/generic/LoadMore'
import { Paging, CommonMatch } from 'types/global'
import { usePaginatedFetch } from 'hooks/use-fetch'
import { DESKTOP_BREAKPOINT } from 'constants/global'
import Score from 'components/generic/Score'

import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { getCommonMatches, clearCommonMatches } from 'actions/h2h-actions'
import useWindowSize from 'hooks/use-windowSize'
import StatusTag from 'components/profile/generic/StatusTag'
import { getProfileUrl, getUnknownDisplayText } from 'utils'
import LockedFeature from 'components/generic/LockedFeature'
import { useTranslation } from 'react-i18next'

const CommonMatchesDesktop = ({
  commonMatches,
  side1PlayerId,
}: { commonMatches: CommonMatch[] } & { side1PlayerId: string }) => {
  const { t } = useTranslation('strMatchHistory')
  return (
    <table>
      <thead>
        <tr>
          <th className={styles.date}>{t('date')}</th>
          <th className={styles.tournament}>{t('tournament')}</th>
          <th className={styles.winner}>{t('winner')}</th>
          <th className={styles.score}>{t('score')}</th>
        </tr>
      </thead>
      <tbody>
        {commonMatches &&
          commonMatches.map(match => (
            <MatchDesktop {...match} side1PlayerId={side1PlayerId} key={match.id} />
          ))}
      </tbody>
    </table>
  )
}

const MatchDesktop = ({
  winner,
  score,
  status,
  tournament,
  start,
  side1PlayerId,
}: CommonMatch & { side1PlayerId: string }) => (
  <tr>
    <td className={styles.date}>{new Date(start).toLocaleDateString()}</td>
    <td className={styles.tournament}>{tournament || getUnknownDisplayText()}</td>
    <td className={styles.winner}>
      <Flag alpha3={winner.countryCode} />
      {side1PlayerId === winner.id ? (
        winner.fullName
      ) : (
        <a href={getProfileUrl(winner.id)} className={`${styles.matchHistoryWinner}`}>
          {winner.fullName}
        </a>
      )}
    </td>
    <td className={`${styles.score} ${styles.greenLink}`}>
      <Score score={score} />
      <StatusTag status={status} />
    </td>
  </tr>
)

const MatchMobile = ({
  winner,
  score,
  status,
  tournament,
  start,
  side1PlayerId,
}: CommonMatch & { side1PlayerId: string }) => {
  const { t } = useTranslation('strMatchHistory')

  return (
    <table className={styles.tableMobile}>
      <thead>
        <tr>
          <th>{new Date(start).toLocaleDateString()}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td className={styles.tournament}>{t('tournament')}</td>
          <td className={styles.tournament}>{tournament || getUnknownDisplayText()}</td>
        </tr>
        <tr>
          <td className={styles.winner}>{t('winner')}</td>
          <td className={styles.winner}>
            {side1PlayerId === winner.id ? (
              winner.fullName
            ) : (
              <a href={getProfileUrl(winner.id)} className={`${styles.matchHistoryWinner}`}>
                {winner.fullName}
              </a>
            )}
          </td>
        </tr>
        <tr>
          <td className={styles.score}>{t('score')}</td>
          <td className={`${styles.score} ${styles.greenLink}`}>
            <Score score={score} />
            <StatusTag status={status} />
          </td>
        </tr>
      </tbody>
    </table>
  )
}

const CommonMatches = ({
  title,
  commonMatches,
  side1PlayerId,
  allLoaded,
  loading,
  loadMatches,
}: {
  title?: string
  commonMatches: CommonMatch[]
  side1PlayerId: string
  allLoaded: boolean
  loading: boolean
  loadMatches?: () => Promise<void>
}) => {
  const windowWidth = useWindowSize().width
  return (
    <div className={`${styles.container} ${styles.matchHistory}`}>
      {title && <h2>{title}</h2>}
      {windowWidth && windowWidth >= DESKTOP_BREAKPOINT && (
        <>
          <CommonMatchesDesktop commonMatches={commonMatches} side1PlayerId={side1PlayerId} />
          {!allLoaded && (
            <div className={styles.loadMoreWrapper}>
              <LoadMore loading={loading} loadMore={loadMatches} />
            </div>
          )}
        </>
      )}

      {windowWidth && windowWidth < DESKTOP_BREAKPOINT && commonMatches && (
        <>
          {commonMatches.map(match => (
            <MatchMobile {...match} side1PlayerId={side1PlayerId} key={match.id} />
          ))}

          {!allLoaded && (
            <div className={styles.loadMoreWrapper}>
              <LoadMore loading={loading} loadMore={loadMatches} />
            </div>
          )}
        </>
      )}
    </div>
  )
}

interface Props {
  playerIds: [string, string]
}

const H2HMatchHistory = ({ playerIds }: Props) => {
  const { t } = useTranslation('strMatchHistory')
  const dispatch = useDispatch()
  const commonMatches: CommonMatch[] = useSelector((state: AppState) => state.h2h.commonMatches)
  const matchesTotal = useSelector((state: AppState) => state.h2h.commonMatchesTotal)
  const matchesLoading = useSelector((state: AppState) => state.h2h.commonMatchesLoading)
  const side1PlayerId = useSelector((state: AppState) => state.h2h.vsPlayerIds[0])
  const clearCommonMatchUps = useMemo(() => () => dispatch(clearCommonMatches()), [dispatch])
  const getCommonMatchUps = useMemo(
    () => (paging: Paging) => dispatch(getCommonMatches(playerIds, paging)),
    [dispatch, playerIds]
  )
  const [loadMatches, loading, allLoaded] = usePaginatedFetch(
    {
      fetch: getCommonMatchUps,
      total: matchesTotal,
      clearResults: clearCommonMatchUps,
      invalidateAutoFetch: !!commonMatches.length,
    },
    [getCommonMatchUps]
  )

  useEffect(() => {
    clearCommonMatchUps()
  }, [clearCommonMatchUps])

  if (!matchesLoading && !(commonMatches && commonMatches.length)) {
    return (
      <>
        <h2>{t('matchHistory')}</h2>
        <p className={styles.noData}>{t('noMatchHistory')}</p>
      </>
    )
  }

  return (
    <CommonMatches
      title={t('matchHistory')}
      commonMatches={commonMatches}
      side1PlayerId={side1PlayerId}
      allLoaded={allLoaded}
      loading={loading}
      loadMatches={loadMatches}
    />
  )
}

export const BlurredMatchHistory = () => {
  const { t } = useTranslation('strMatchHistory')
  const playerId = 'id'
  const windowWidth = useWindowSize().width ?? 0
  const isDesktop = windowWidth >= DESKTOP_BREAKPOINT
  const commonMatches = useMemo(() => {
    const date = new Date().toDateString()
    const generateName = (name: string) => {
      let index = Math.floor(Math.random() * (name.length - 3)) + 3
      if (index > name.length - 1) index = name.length - 1
      return name.slice(0, index) + ' ' + name.slice(index + 1)
    }
    const numberOfMatches = isDesktop ? 3 : 2
    return new Array(numberOfMatches).fill(true).map((item, index) => ({
      id: index,
      score: {
        sets: [
          {
            loserGamesWon: 4,
            tiebreaker: null,
            winnerGamesWon: 6,
          },
          { loserGamesWon: 4, tiebreaker: null, winnerGamesWon: 6 },
        ],

        superTiebreak: null,
      },
      start: date,
      tournament: generateName('Tournamentname'),
      winner: {
        fullName: generateName('Winnerplayername'),
        id: playerId,
      },
    }))
  }, [isDesktop]) as unknown as CommonMatch[]

  return (
    <>
      <h2>{t('matchHistory')}</h2>
      <LockedFeature
        info={{ ns: 'strMessageScreen', transKey: 'lockedFeature.blurredMatchHistory' }}
      >
        <CommonMatches
          commonMatches={commonMatches}
          side1PlayerId={playerId}
          allLoaded
          loading={false}
        />
      </LockedFeature>
    </>
  )
}

export default H2HMatchHistory
